import { useTranslation } from "react-i18next";
import {
  useCreateAiResistantHomework,
  useGetAiResistantHomework,
  getAiResistantHomework,
} from "@packages/api/madlenAPI";
import { Shield } from "lucide-react";
import { AiResistantHomeworkResponseDto } from "@packages/api/madlenAPI.schemas";
import { AiHomeworkIdeaCard } from "../../../components/ai-resistant-homework-ideas/AiHomeworkIdeaCard";
import GenericToolCreationPage from "../../../components/tool_components/GenericToolCreationPage";
import {
  AiResistantHomeworkCreateModal,
  CreateModalProps,
} from "@features/tools/components/create-modal/modals";
import i18n from "@packages/i18n/config";

const AiResistantHomeworkIdeaView = () => {
  const { t } = useTranslation();

  const CreateModalWrapper = ({
    initialInput,
    onSubmit,
    isLoading,
    error,
  }: Partial<CreateModalProps>) => (
    <AiResistantHomeworkCreateModal
      title={t("aiHomeworkIdeas.form.generateButton")}
      backPath="/content/ai-resistant-homework-ideas"
      loadingTitle={t("aiHomeworkIdeas.form.loading")}
      errorTitle={t("aiHomeworkIdeas.result.error.title")}
      errorDescription={t("aiHomeworkIdeas.result.error.loadingError")}
      submitButtonText={t("common.generate")}
      initialInput={initialInput!}
      onSubmit={onSubmit!}
      isLoading={isLoading!}
      error={error!}
      after={
        <div className="flex items-center space-x-2 bg-emerald-100 dark:bg-emerald-900/50 p-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
          <Shield className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <p className="text-sm text-emerald-600 dark:text-emerald-400">
            {t("aiHomeworkIdeas.aiResistant")}
          </p>
        </div>
      }
    />
  );

  const ToolCardWrapper = ({
    toolData,
    isImageLoading,
    onCreateAgain,
  }: {
    toolData: AiResistantHomeworkResponseDto;
    isImageLoading: boolean;
    onCreateAgain: () => void;
  }) => {
    const formattedHomeworkIdea = {
      id: toolData.id!.toString(),
      imageUrl: toolData.imageUrl || "",
      title: toolData.title || "",
      topic: toolData.title || toolData.prompt || "",
      subject: toolData.courseName || "",
      gradeLevel: toolData.gradeLevel || 0,
      duration: "45",
      difficulty: "medium",
      description: toolData.description || "",
      ideas: Array.isArray(toolData.reportIdeas) ? toolData.reportIdeas : [],
      authorName: toolData.authorName || "",
      authorId: toolData.authorId?.toString() || "",
      likeCount: toolData.likeCount || 0,
      didUserLiked: toolData.didUserLiked || false,
      createdAt: toolData.createdAt || "",
      status: toolData.status || "",
      contentLanguage: toolData.contentLanguage || i18n.language,
    };

    return (
      <AiHomeworkIdeaCard
        homeworkIdea={formattedHomeworkIdea}
        isImageLoading={isImageLoading}
        onCreateAgain={onCreateAgain}
      />
    );
  };

  return (
    <GenericToolCreationPage
      toolType="ai-resistant-homework-idea"
      useCreateTool={useCreateAiResistantHomework}
      useGetTool={useGetAiResistantHomework}
      getTool={getAiResistantHomework}
      ToolCard={ToolCardWrapper}
      CreateModalWrapper={CreateModalWrapper}
      toolDataExtractor={(data: AiResistantHomeworkResponseDto) =>
        data?.reportIdeas && data?.reportIdeas?.length > 0
      }
    />
  );
};

export default AiResistantHomeworkIdeaView;
